import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  GraduationCap, Search, ArrowLeft, FileText, IdCard,
  Download, MessageCircle, TrendingUp, TrendingDown, Minus,
  CalendarX, Sparkles, Info, Loader2, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const pub = axios.create({ baseURL: API_BASE, withCredentials: false });

/* ============================================================
 * ENTRY /parent : formulaire matricule
 * ============================================================ */
export function ParentEntry() {
  const [matricule, setMatricule] = useState("");
  const navigate = useNavigate();

  const submit = (e) => {
    e.preventDefault();
    const m = matricule.trim().toUpperCase();
    if (!m) return;
    navigate(`/parent/${m}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1F7A63] to-[#155744] grid place-items-center p-4">
      <div className="max-w-md w-full">
        <Link to="/" className="flex items-center gap-2 text-white mb-8 justify-center">
          <div className="w-10 h-10 rounded-lg bg-white grid place-items-center">
            <GraduationCap className="w-6 h-6 text-[#1F7A63]" />
          </div>
          <span className="text-xl font-bold" style={{ fontFamily: "Outfit" }}>EDUKATI</span>
        </Link>
        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
          <div className="text-center mb-6">
            <div className="w-14 h-14 rounded-full bg-[#E8F2EF] grid place-items-center mx-auto mb-3">
              <IdCard className="w-7 h-7 text-[#1F7A63]" />
            </div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>Espace parent</h1>
            <p className="text-sm text-[#5C736A] mt-1">
              Entrez le matricule de votre enfant pour consulter ses résultats.
            </p>
          </div>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="text-sm font-medium mb-1 block">Matricule de l&apos;élève</label>
              <Input
                data-testid="parent-matricule-input"
                value={matricule}
                onChange={(e) => setMatricule(e.target.value.toUpperCase())}
                placeholder="Ex : EECG2622B5"
                className="text-lg tracking-wider font-mono"
                autoFocus
              />
              <p className="text-xs text-[#5C736A] mt-1.5">
                Le matricule figure sur le bulletin et la fiche élève.
              </p>
            </div>
            <Button
              type="submit"
              data-testid="parent-search-btn"
              className="w-full bg-[#1F7A63] hover:bg-[#155744] text-white gap-2"
              size="lg"
            >
              <Search className="w-4 h-4" /> Consulter la fiche
            </Button>
          </form>
        </div>
        <div className="text-center mt-6">
          <Link to="/" className="text-white/80 text-sm hover:text-white flex items-center justify-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Retour à l&apos;accueil
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
 * /parent/:matricule — affichage fiche
 * ============================================================ */
export function ParentFiche() {
  const { matricule } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    pub.get(`/parent/${matricule}`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(e.response?.data?.detail || "Élève introuvable"))
      .finally(() => setLoading(false));
  }, [matricule]);

  if (loading) return (
    <div className="min-h-screen bg-[#F6F8F9] grid place-items-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#1F7A63]" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#F6F8F9] grid place-items-center p-4">
      <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow">
        <div className="w-14 h-14 rounded-full bg-red-100 grid place-items-center mx-auto mb-3">
          <Info className="w-7 h-7 text-red-600" />
        </div>
        <h1 className="text-lg font-bold mb-2">Élève introuvable</h1>
        <p className="text-sm text-[#5C736A] mb-4">{error}</p>
        <Link to="/parent"><Button className="bg-[#1F7A63] hover:bg-[#155744] text-white">Réessayer</Button></Link>
      </div>
    </div>
  );

  return <ParentFicheContent data={data} matricule={matricule} />;
}


/* ============================================================
 * /parent/card/:token — accès sécurisé via QR de la carte scolaire
 * ============================================================ */
export function ParentByToken() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    pub.get(`/parent/card/${token}`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(e.response?.data?.detail || "Lien invalide ou désactivé."))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return (
    <div className="min-h-screen bg-[#F6F8F9] grid place-items-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#1F7A63]" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#F6F8F9] grid place-items-center p-4">
      <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow">
        <div className="w-14 h-14 rounded-full bg-red-100 grid place-items-center mx-auto mb-3">
          <Info className="w-7 h-7 text-red-600" />
        </div>
        <h1 className="text-lg font-bold mb-2">Accès refusé</h1>
        <p className="text-sm text-[#5C736A] mb-4">{error}</p>
        <Link to="/parent"><Button className="bg-[#1F7A63] hover:bg-[#155744] text-white">Utiliser un matricule</Button></Link>
      </div>
    </div>
  );

  return <ParentFicheContent data={data} matricule={data?.student?.matricule} />;
}

function ParentFicheContent({ data, matricule }) {
  const { student: st, class: cls, school, maitre, moyennes, evaluations, absences, evolution, indice, commentaire, vie_scolaire = [] } = data;
  const [t, setT] = useState("T1");

  const indiceColor = indice == null ? "#5C736A"
    : indice >= 8 ? "#1F7A63"
    : indice >= 6 ? "#4a9078"
    : indice >= 4 ? "#E6B800"
    : "#D93838";

  const bulletinUrl = (tri) => `${API_BASE}/parent/${matricule}/bulletin.pdf?trimestre=${tri}&mode=1`;
  const ficheUrl = `${API_BASE}/parent/${matricule}/fiche.pdf`;

  const contactMaitre = () => {
    const tel = String(maitre?.telephone || "").replace(/\D/g, "");
    if (!tel) return toast.info("Aucun numéro de contact du maître n'est disponible.");
    const msg = encodeURIComponent(`Bonjour, je suis le parent de ${st.prenom} ${st.nom} (${st.matricule}). Je souhaite échanger avec vous.`);
    const wa = `https://wa.me/${tel.startsWith("221") ? tel : "221" + tel}?text=${msg}`;
    window.open(wa, "_blank");
  };

  return (
    <div className="min-h-screen bg-[#F6F8F9]">
      {/* Top bar */}
      <div className="bg-gradient-to-r from-[#1F7A63] to-[#155744] text-white">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/parent" className="flex items-center gap-2 text-white/90 hover:text-white">
            <ArrowLeft className="w-5 h-5" /> <span className="text-sm">Autre élève</span>
          </Link>
          <div className="flex items-center gap-2 text-sm">
            <GraduationCap className="w-4 h-4" />
            <span style={{ fontFamily: "Outfit" }} className="font-semibold">EDUKATI</span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-4">
        {/* Header student card */}
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="w-16 h-16 rounded-full bg-[#1F7A63] text-white grid place-items-center text-2xl font-bold shrink-0">
              {(st.prenom || "?").slice(0, 1)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xl sm:text-2xl font-bold" style={{ fontFamily: "Outfit" }}>
                <span className="font-normal">{st.prenom}</span> {st.nom}
              </div>
              <div className="text-sm text-[#5C736A] mt-1">
                Matricule : <span className="font-mono font-semibold text-[#1F7A63]">{st.matricule}</span>
              </div>
              <div className="text-xs text-[#5C736A] mt-0.5">
                {cls?.nom || "—"} · {school?.nom || "—"}
              </div>
            </div>
            <div className="text-center">
              <div className="text-xs text-[#5C736A] uppercase tracking-wider">Indice</div>
              <div className="text-4xl font-bold" style={{ fontFamily: "Outfit", color: indiceColor }}>
                {indice != null ? indice : "—"}<span className="text-lg text-[#5C736A]">/10</span>
              </div>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <a href={ficheUrl} target="_blank" rel="noreferrer" data-testid="btn-fiche-pdf">
            <Button className="w-full bg-white border border-[#E2E8F0] text-[#1A2E28] hover:bg-slate-50 gap-1 h-auto py-3 flex-col">
              <IdCard className="w-5 h-5 text-[#1F7A63]" />
              <span className="text-xs">Fiche PDF</span>
            </Button>
          </a>
          <div>
            <select
              className="w-full h-full bg-white border border-[#E2E8F0] rounded-md text-xs px-2 py-2 hover:bg-slate-50 cursor-pointer text-center"
              value={t}
              onChange={(e) => setT(e.target.value)}
              data-testid="trim-select"
            >
              <option value="T1">Bulletin T1</option>
              <option value="T2">Bulletin T2</option>
              <option value="T3">Bulletin T3</option>
            </select>
          </div>
          <a href={bulletinUrl(t)} target="_blank" rel="noreferrer" data-testid="btn-bulletin-pdf">
            <Button className="w-full bg-[#E6B800] hover:bg-[#c39c00] text-[#1A2E28] gap-1 h-auto py-3 flex-col">
              <FileText className="w-5 h-5" />
              <span className="text-xs">Télécharger {t}</span>
            </Button>
          </a>
          <Button
            onClick={contactMaitre}
            data-testid="btn-contact-maitre"
            className="w-full bg-[#25D366] hover:bg-[#1ea952] text-white gap-1 h-auto py-3 flex-col"
          >
            <MessageCircle className="w-5 h-5" />
            <span className="text-xs">Contacter maître</span>
          </Button>
        </div>

        {maitre?.name && (
          <div className="text-xs text-[#5C736A] text-center">
            Maître de la classe : <b>{maitre.name}</b>
          </div>
        )}

        {/* Moyennes */}
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            Notes & moyennes {evolution != null && (
              evolution > 0.5
                ? <span className="text-[#1F7A63] text-xs flex items-center gap-1"><TrendingUp className="w-3 h-3" /> +{evolution}</span>
                : evolution < -0.5
                  ? <span className="text-red-600 text-xs flex items-center gap-1"><TrendingDown className="w-3 h-3" /> {evolution}</span>
                  : <span className="text-[#5C736A] text-xs flex items-center gap-1"><Minus className="w-3 h-3" /> stable</span>
            )}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <MoyCard label="T1" value={moyennes.T1} />
            <MoyCard label="T2" value={moyennes.T2} />
            <MoyCard label="T3" value={moyennes.T3} />
            <MoyCard label="Annuelle" value={moyennes.annuelle} highlight />
          </div>
          {evaluations.length > 0 && (
            <div className="mt-4">
              <div className="text-xs text-[#5C736A] uppercase tracking-wider mb-2">Évaluations récentes</div>
              <div className="space-y-1.5">
                {evaluations.map((e, i) => (
                  <div key={i} className="flex items-center justify-between text-sm px-3 py-2 bg-slate-50 rounded-lg">
                    <span>{e.session_nom}</span>
                    <span className="font-semibold">{e.total.toFixed(1)} / {e.bareme} <span className="text-[#5C736A] text-xs">({e.pct}%)</span></span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Absences */}
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <CalendarX className="w-5 h-5 text-red-500" /> Absences
          </h3>
          <div className="grid grid-cols-2 gap-3 text-center">
            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="text-xs text-[#5C736A]">Total</div>
              <div className="text-2xl font-bold">{absences.total}</div>
            </div>
            <div className="p-3 bg-[#E8F2EF]/50 rounded-lg">
              <div className="text-xs text-[#5C736A]">Justifiées</div>
              <div className="text-2xl font-bold text-[#1F7A63]">{absences.justifiees}</div>
            </div>
          </div>
        </div>

        <VieScolaireBlock events={vie_scolaire} />

        {/* Commentaires */}
        <div className="bg-[#FCF8E6]/60 border-l-4 border-[#E6B800] rounded-2xl p-5 shadow-sm">
          <h3 className="font-semibold mb-2 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Sparkles className="w-5 h-5 text-[#E6B800]" /> Conseil pédagogique
          </h3>
          <ul className="space-y-1.5 text-sm text-[#1A2E28]">
            {commentaire.map((c, i) => (
              <li key={i} className="flex items-start gap-2">
                <Info className="w-4 h-4 text-[#1F7A63] shrink-0 mt-0.5" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="text-center py-4">
          <div className="text-xs text-[#5C736A]">EDUKATI © {new Date().getFullYear()} — Espace parent</div>
        </div>
      </div>
    </div>
  );
}

function MoyCard({ label, value, highlight }) {
  return (
    <div className={`p-3 rounded-lg text-center ${highlight ? "bg-[#E8F2EF] border border-[#1F7A63]/30" : "bg-slate-50"}`}>
      <div className="text-xs text-[#5C736A]">{label}</div>
      <div className={`text-xl font-bold ${highlight ? "text-[#1F7A63]" : ""}`} style={{ fontFamily: "Outfit" }}>
        {value != null ? value.toFixed(2) : "—"}<span className="text-xs text-[#5C736A]">/10</span>
      </div>
    </div>
  );
}

const EVENT_LABELS = {
  sanction: ["Sanction", "bg-red-50 text-red-700"],
  recompense: ["Récompense", "bg-[#E8F2EF] text-[#1F7A63]"],
  comportement_positif: ["Comportement positif", "bg-[#E8F2EF] text-[#1F7A63]"],
  comportement_negatif: ["Comportement à améliorer", "bg-orange-50 text-orange-700"],
  retard: ["Retard", "bg-amber-50 text-amber-700"],
  message_parent: ["Message", "bg-slate-100 text-slate-700"],
};

function VieScolaireBlock({ events }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm p-5" data-testid="parent-vie-scolaire">
      <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
        <ShieldAlert className="w-5 h-5 text-[#1F7A63]" /> Vie scolaire
      </h3>
      {events.length === 0 ? (
        <div className="text-sm text-[#5C736A]" data-testid="parent-vie-scolaire-empty">Aucun événement enregistré.</div>
      ) : (
        <div className="space-y-1.5">
          {events.map((e) => {
            const [label, cls] = EVENT_LABELS[e.type] || [e.type, "bg-slate-100 text-slate-700"];
            return (
              <div key={e.id} className="flex items-start gap-3 text-sm px-3 py-2 bg-slate-50 rounded-lg" data-testid={`parent-event-${e.id}`}>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full shrink-0 ${cls}`}>{label}</span>
                <div className="flex-1 min-w-0">
                  <div>{[e.categorie, e.description].filter(Boolean).join(" — ") || "—"}</div>
                  {e.recommandations && <div className="text-xs text-[#5C736A] mt-0.5">{e.recommandations}</div>}
                </div>
                <span className="text-xs text-[#5C736A] shrink-0">{e.date ? new Date(e.date).toLocaleDateString("fr-FR") : ""}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
